import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import API_CONFIG from './config/api';

function DetalhesSimulacao({ simulacaoId, onVoltar }) {
  const API_URL = `${API_CONFIG.API_URL}`;
  
  const [simulacao, setSimulacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [mensagemSucesso, setMensagemSucesso] = useState('');
  const [mensagemErro, setMensagemErro] = useState('');
  const [pendingSavesCount, setPendingSavesCount] = useState(0);
  const sucessoRef = useRef(null);
  const [campoCopiadoFeedback, setCampoCopiadoFeedback] = useState('');
  const [mostrarFichaCompleta, setMostrarFichaCompleta] = useState(false);
  const [bancos, setBancos] = useState([]);
  const [bancoAtualIndex, setBancoAtualIndex] = useState(-1);
  const [simulacoesBancos, setSimulacoesBancos] = useState({});
  const [expandedHeaderMobile, setExpandedHeaderMobile] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mostrarModalConfirmacaoBancos, setMostrarModalConfirmacaoBancos] = useState(false);
  const [bancosPendentes, setBancosPendentes] = useState([]);
  const [fichaFoiPreenchida, setFichaFoiPreenchida] = useState(false);
  const [carregandoStatusFicha, setCarregandoStatusFicha] = useState(false);
  const [mostrarResumo, setMostrarResumo] = useState(false);
  
  // Obter GAP do usuário
  const usuarioLogado = JSON.parse(localStorage.getItem('user') || localStorage.getItem('usuario') || '{}');
  const gapParcela = usuarioLogado.permissoes?.configuracoes_pessoais?.gap_parcela || 0;
  const isVendedor = usuarioLogado.permissoes?.tipo === 'Vendedor';
  
  // Estado para observações
  const [novaObservacao, setNovaObservacao] = useState('');
  const [adicionandoObservacao, setAdicionandoObservacao] = useState(false);

  const getValorComGap = (valorOriginal) => {
    if (!gapParcela || !valorOriginal) return valorOriginal;
    
    // Tenta extrair o número
    const apenasDigitos = valorOriginal.toString().replace(/\D/g, '');
    if (!apenasDigitos) return valorOriginal;
    
    const numero = parseFloat(apenasDigitos) / 100;
    const comGap = numero + parseFloat(gapParcela);
    
    return comGap.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  useEffect(() => {
    carregarSimulacao();
    carregarBancos();
  }, [simulacaoId]);

  // Sempre resetar a visualização completa ao abrir uma nova simulação
  useEffect(() => {
    setMostrarFichaCompleta(false);
  }, [simulacaoId]);

  // Detectar mudanças de tamanho da tela para mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Escutar eventos Socket.IO de alternância de banco
  useEffect(() => {
    const socket = io(API_CONFIG.API_URL.replace('/api', ''), {
      transports: ['websocket', 'polling'],
      reconnection: true
    });

    socket.on('connect', () => {
    });

    // Escutar evento genérico de alternância
    socket.on('alternar_banco', (payload) => {
      alternarParaBanco(payload.bancoId, payload.codigoBanco, payload.nome);
    });

    // Escutar eventos específicos por banco (alternar_bv, alternar_itau, etc)
    const limparListeners = [];
    bancos.forEach(banco => {
      const slug = banco.nome
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      
      const eventName = `alternar_${slug}`;
      
      const handler = (payload) => {
        alternarParaBanco(payload.bancoId, payload.codigoBanco, payload.nome);
      };
      
      socket.on(eventName, handler);
      limparListeners.push(() => socket.off(eventName, handler));
    });

    return () => {
      limparListeners.forEach(limpar => limpar());
      socket.disconnect();
    };
  }, [bancos]);

  const alternarParaBanco = (bancoId, codigoBanco, nomeBanco) => {
    // Priorizar busca por codigo_banco, depois _id, e por último nome
    let index = -1;
    
    if (codigoBanco) {
      index = bancos.findIndex(b => b.codigo_banco === codigoBanco);
    }
    
    if (index === -1 && bancoId) {
      index = bancos.findIndex(b => b._id === bancoId);
    }
    
    if (index === -1 && nomeBanco) {
      index = bancos.findIndex(b => b.nome === nomeBanco);
    }
    
    if (index !== -1) {
      setBancoAtualIndex(index);
    } else {
    }
  };

  const carregarBancos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/bancos`, {
        headers: { 'x-auth-token': token }
      });
      
      if (response.data.success) {
        const bancosAtivos = response.data.data.filter(b => b.ativo);
        setBancos(bancosAtivos);
        
        // NÃO reseta simulacoesBancos aqui - será preenchido ao carregar a simulação
        // Apenas inicializa bancos que ainda não têm dados
        setSimulacoesBancos(prev => {
          const novoEstado = { ...prev };
          bancosAtivos.forEach(banco => {
            // Só inicializa se o banco não existe ainda no estado
            if (!novoEstado[banco._id]) {
              novoEstado[banco._id] = { 
                valorLiberado: '', 
                opcoes: [],
                recusada: false,
                entradaMinima: ''
              };
            }
          });
          return novoEstado;
        });
      }
    } catch (error) {
    }
  };

  const carregarSimulacao = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/simulacoes/${simulacaoId}`, {
        headers: { 'x-auth-token': token }
      });

      if (response.data.success) {
        const sim = response.data.data;
        
        // Formatar data para exibição dd/mm/yyyy evitando problemas de timezone
        let nascimentoFormatado = '';
        if (sim.nascimento) {
          // Se for string ISO (ex: 1990-01-29T00:00:00.000Z), pegar apenas a parte da data
          const dataStr = typeof sim.nascimento === 'string' ? sim.nascimento.split('T')[0] : '';
          if (dataStr.includes('-')) {
            const [ano, mes, dia] = dataStr.split('-');
            nascimentoFormatado = `${dia}/${mes}/${ano}`;
          } else {
            const data = new Date(sim.nascimento);
            const dia = String(data.getUTCDate()).padStart(2, '0');
            const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
            const ano = data.getUTCFullYear();
            nascimentoFormatado = `${dia}/${mes}/${ano}`;
          }
        }
        
        // Carregar simulações dos bancos se existirem
        if (sim.simulacoes_bancos && Object.keys(sim.simulacoes_bancos).length > 0) {
          const simulacoesFormatadas = {};
          Object.entries(sim.simulacoes_bancos).forEach(([bancoId, dados]) => {
            // Verificar se é nova estrutura (objeto) ou antiga (array)
            if (Array.isArray(dados)) {
              // Estrutura antiga - converter para nova
              simulacoesFormatadas[bancoId] = {
                valorLiberado: dados[0]?.valorLiberado ? formatarMoeda(dados[0].valorLiberado) : '',
                opcoes: dados.map(linha => ({
                  qtdParcelas: linha.qtdParcelas || linha.parcelas || '48',
                  valorParcela: linha.valorParcela ? formatarMoeda(linha.valorParcela) : (linha.valor ? formatarMoeda(linha.valor) : '')
                })),
                recusada: !!dados[0]?.recusada,
                entradaMinima: dados[0]?.entradaMinima ? formatarMoeda(dados[0].entradaMinima) : ''
              };
            } else {
              // Estrutura nova
              simulacoesFormatadas[bancoId] = {
                valorLiberado: dados.valorLiberado ? formatarMoeda(dados.valorLiberado) : '',
                opcoes: (dados.opcoes || []).map(op => ({
                  qtdParcelas: op.qtdParcelas || op.parcelas || '48',
                  valorParcela: (op.valorParcela !== undefined && op.valorParcela !== null && op.valorParcela !== '')
                    ? formatarMoeda(op.valorParcela)
                    : ((op.valor !== undefined && op.valor !== null && op.valor !== '') ? formatarMoeda(op.valor) : '')
                })),
                recusada: !!dados.recusada,
                entradaMinima: dados.entradaMinima ? formatarMoeda(dados.entradaMinima) : ''
              };
            }
          });
          setSimulacoesBancos(simulacoesFormatadas);
        }
        
        setSimulacao({
          ...sim,
          nascimento: nascimentoFormatado,
          valorVenda: sim.valorVenda ? formatarMoedaDireta(sim.valorVenda) : '',
          entrada: sim.entrada ? formatarMoedaDireta(sim.entrada) : '',
          financiado: sim.financiado ? formatarMoedaDireta(sim.financiado) : ''
        });
        
        // Verificar status de preenchimento da ficha
        if (sim.ficha_id?._id) {
          verificarStatusPreenchimentoFicha(sim.ficha_id._id);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Função para verificar se a ficha foi preenchida
  const verificarStatusPreenchimentoFicha = async (fichaId) => {
    try {
      setCarregandoStatusFicha(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/fichas/${fichaId}/status-preenchimento`, {
        headers: { 'x-auth-token': token }
      });

      if (response.data.success) {
        setFichaFoiPreenchida(response.data.data.foiPreenchida);
      }
    } catch (error) {
      console.error('Erro ao verificar status de preenchimento:', error);
    } finally {
      setCarregandoStatusFicha(false);
    }
  };

  const formatarMoeda = (valor) => {
    if (!valor) return '';
    const numero = valor.toString().replace(/\D/g, '');
    if (!numero) return '';
    const valorNumerico = parseFloat(numero) / 100;
    return valorNumerico.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatarMoedaDireta = (valor) => {
    if (!valor) return '';
    // Se já for string formatada, retornar
    if (typeof valor === 'string' && valor.includes('R$')) return valor;
    // Converter número direto para moeda
    const valorNumerico = parseFloat(valor);
    if (isNaN(valorNumerico)) return '';
    return valorNumerico.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // ----- Persistência de saves em fila para evitar perda de dados -----
  const PENDING_SAVES_KEY = 'vetim_pending_saves_v2';

  const loadPendingSaves = () => {
    try {
      const raw = localStorage.getItem(PENDING_SAVES_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (err) {
      return {};
    }
  };

  const savePendingSaves = (queue) => {
    try {
      localStorage.setItem(PENDING_SAVES_KEY, JSON.stringify(queue));
    } catch (err) {
    }
  };

  const enqueueSave = (simId, payload) => {
    const queue = loadPendingSaves();
    if (!queue[simId]) queue[simId] = [];
    queue[simId].push({ id: Date.now() + '_' + Math.random().toString(36).slice(2,8), payload, attempts: 0, lastAttempt: 0 });
    savePendingSaves(queue);
    recomputePendingCount();
  };

  const removeQueuedItem = (simId, itemId) => {
    const queue = loadPendingSaves();
    if (!queue[simId]) return;
    queue[simId] = queue[simId].filter(i => i.id !== itemId);
    if (queue[simId].length === 0) delete queue[simId];
    savePendingSaves(queue);
    recomputePendingCount();
  };

  const recomputePendingCount = () => {
    const q = loadPendingSaves();
    let total = 0;
    Object.values(q).forEach(arr => { total += arr.length; });
    setPendingSavesCount(total);
  };

  // Processar fila de pending saves — tenta reenviar até sucesso
  const processPendingSaves = async () => {
    const queue = loadPendingSaves();
    const token = localStorage.getItem('token');
    const now = Date.now();

    for (const simId of Object.keys(queue)) {
      const items = queue[simId];
      for (const item of items) {
        // Exponential backoff simples: esperar 2^attempts * 2s
        const delay = Math.min(60000, Math.pow(2, Math.min(6, item.attempts)) * 2000);
        if (item.lastAttempt && (now - item.lastAttempt) < delay) continue;

        try {
          item.attempts += 1;
          item.lastAttempt = now;
          savePendingSaves(queue);

          const response = await axios.put(`${API_URL}/simulacoes/${simId}`, item.payload, { headers: { 'x-auth-token': token } });
          if (response.data && response.data.success) {
            // Remover item da fila e recarregar sim
            removeQueuedItem(simId, item.id);
            try { await carregarSimulacao(); } catch(e) {}
          }
        } catch (err) {
          // Em caso de 429, não aumentar tentativas agressivamente — apenas aguardar próxima iteração
          if (err?.response?.status === 429) {
            continue;
          }

          // Se attempts exceder um número alto (ex: 10), manter o item mas avisar no console — não deletar automaticamente
          if (item.attempts > 10) {
          }
        }
      }
    }
  };

  // Iniciar worker para processar fila periodicamente
  useEffect(() => {
    const interval = setInterval(() => {
    }, 5000);

    // Executar imediatamente uma vez
    processPendingSaves().catch(err => {});

    // Inicializar contagem
    recomputePendingCount();

    // Recalcular quando localStorage muda (outros tabs)
    const onStorage = (e) => {
      if (e.key === PENDING_SAVES_KEY) recomputePendingCount();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const desformatarMoeda = (valor) => {
    if (!valor) return '';
    // Remove tudo exceto números
    return valor.replace(/\D/g, '');
  };

  const desformatarMoedaParaDecimal = (valor) => {
    if (!valor) return 0;
    // Se for número, retornar direto
    if (typeof valor === 'number') return valor;
    // Remove R$, pontos e espaços, substitui vírgula por ponto
    const valorLimpo = valor
      .replace(/R\$/g, '')
      .replace(/\s/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
    const numero = parseFloat(valorLimpo);
    return isNaN(numero) ? 0 : numero;
  };

  const limparValorParaCopia = (campo, valor) => {
    if (!valor) return '';
    
    // Para data de nascimento: manter formato xx/xx/xxxx
    if (campo === 'nascimento') {
      return valor; 
    }
    
    // Para valores monetários: remover R$ e espaços, manter formato 32900,00
    if (['valorVenda', 'entrada', 'financiado'].includes(campo)) {
      // Remove R$ e espaços
      let valorLimpo = valor.replace(/[R$\s]/g, '');
      // Remove pontos de milhar
      valorLimpo = valorLimpo.replace(/\./g, '');
      // Se não tiver vírgula (sem centavos), adicionar ,00
      if (!valorLimpo.includes(',')) {
        valorLimpo = valorLimpo + ',00';
      }
      return valorLimpo;
    }
    
    // Para CPF, RG, telefones, CEP e outros números: remover pontos, traços, barras, parênteses e espaços
    if (['cpf', 'rg', 'celular', 'telefone', 'telefone_comercial', 'cep', 'cnpj_empresa'].includes(campo)) {
      return valor.replace(/\D/g, ''); // Remove tudo que não é número
    }
    
    return valor;
  };

  const adicionarOpcaoParcela = (bancoId) => {
    setSimulacoesBancos(prev => {
      const banco = prev[bancoId] || { valorLiberado: '', opcoes: [], recusada: false, entradaMinima: '' };
      return {
        ...prev,
        [bancoId]: {
          ...banco,
          opcoes: [...banco.opcoes, { qtdParcelas: '48', valorParcela: '' }]
        }
      };
    });
  };

  const atualizarValorLiberado = (bancoId, valor) => {
    setSimulacoesBancos(prev => {
      const banco = prev[bancoId] || { valorLiberado: '', opcoes: [], recusada: false, entradaMinima: '' };
      const valorFormatado = formatarMoeda(valor);
      const opcoes = banco.opcoes && banco.opcoes.length > 0
        ? banco.opcoes
        : (valorFormatado ? [{ qtdParcelas: '48', valorParcela: '' }] : []);
      return {
        ...prev,
        [bancoId]: {
          ...banco,
          valorLiberado: valorFormatado,
          opcoes
        }
      };
    });
  };

  const atualizarOpcaoParcela = (bancoId, opcaoIndex, campo, valor) => {
    setSimulacoesBancos(prev => {
      const banco = prev[bancoId] || { valorLiberado: '', opcoes: [], recusada: false, entradaMinima: '' };
      const novasOpcoes = [...banco.opcoes];
      let valorFormatado = valor;
      
      if (campo === 'valorParcela') {
        valorFormatado = formatarMoeda(valor);
      }
      
      novasOpcoes[opcaoIndex] = { ...novasOpcoes[opcaoIndex], [campo]: valorFormatado };
      return {
        ...prev,
        [bancoId]: {
          ...banco,
          opcoes: novasOpcoes
        }
      };
    });
  };

  const removerOpcaoParcela = (bancoId, opcaoIndex) => {
    setSimulacoesBancos(prev => {
      const banco = prev[bancoId] || { valorLiberado: '', opcoes: [], recusada: false, entradaMinima: '' };
      const novasOpcoes = [...banco.opcoes];
      novasOpcoes.splice(opcaoIndex, 1);
      return { 
        ...prev, 
        [bancoId]: {
          ...banco,
          opcoes: novasOpcoes
        }
      };
    });
  };

  const toggleRecusada = (bancoId) => {
    setSimulacoesBancos(prev => {
      const banco = prev[bancoId] || { valorLiberado: '', opcoes: [], recusada: false, entradaMinima: '' };
      return {
        ...prev,
        [bancoId]: {
          ...banco,
          recusada: !banco.recusada
        }
      };
    });
  };

  const navegarBanco = (bancoId) => {
    const index = bancos.findIndex(b => b._id === bancoId);
    if (index !== -1) {
      setBancoAtualIndex(index);
    }
  };

  const handleChange = (campo, valor) => {
    let valorFormatado = valor;

    // Formatar campos monetários
    if (['valorVenda', 'entrada'].includes(campo)) {
      valorFormatado = formatarMoeda(valor);
    }

    setSimulacao(prev => {
      const novosDados = { ...prev, [campo]: valorFormatado };

      // Recalcular financiado automaticamente
      if (campo === 'valorVenda' || campo === 'entrada') {
        const venda = desformatarMoedaParaDecimal(campo === 'valorVenda' ? valorFormatado : novosDados.valorVenda);
        const entrada = desformatarMoedaParaDecimal(campo === 'entrada' ? valorFormatado : novosDados.entrada);
        const financiado = venda - entrada;
        novosDados.financiado = financiado > 0 ? formatarMoedaDireta(financiado) : '';
      }

      return novosDados;
    });
  };

  const adicionarObservacao = async () => {
    if (!novaObservacao.trim()) {
      console.error('Observação vazia');
      return;
    }

    try {
      setAdicionandoObservacao(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/simulacoes/${simulacaoId}/observacoes`,
        { texto: novaObservacao },
        { headers: { 'x-auth-token': token } }
      );

      if (response.data.success) {
        setSimulacao(prev => ({
          ...prev,
          observacoes: response.data.observacoes
        }));
        setNovaObservacao('');
      }
    } catch (error) {
      console.error('Erro ao adicionar observação:', error);
      alert('Erro ao adicionar observação');
    } finally {
      setAdicionandoObservacao(false);
    }
  };

  const handleDuploClique = async (campo, valor) => {
    if (!valor) return;

    try {
      // Limpar valor antes de copiar
      const valorLimpo = limparValorParaCopia(campo, valor);
      
      // Copiar para área de transferência
      await navigator.clipboard.writeText(valorLimpo);
      
      // Mostrar feedback visual
      setCampoCopiadoFeedback(campo);
      
      // Remover feedback após 1 segundo
      setTimeout(() => {
        setCampoCopiadoFeedback('');
      }, 1000);
    } catch (error) {
    }
  };

  // Atualizar campos aninhados (ex: ficha_id.cliente.rg)
  const handleNestedChange = (path, valor) => {
    setSimulacao(prev => {
      const novo = { ...prev };
      const chaves = path.split('.');
      let alvo = novo;
      for (let i = 0; i < chaves.length - 1; i++) {
        const k = chaves[i];
        if (!alvo[k]) alvo[k] = {};
        alvo = alvo[k];
      }
      alvo[chaves[chaves.length - 1]] = valor;
      return novo;
    });
  };

  const handleSalvar = async () => {
    let dadosParaEnviar = null;
    try {
      setSalvando(true);
      const token = localStorage.getItem('token');

      // Converter data dd/mm/yyyy para formato ISO (yyyy-mm-dd)
      let nascimentoISO = simulacao.nascimento;
      if (typeof simulacao.nascimento === 'string' && simulacao.nascimento.includes('/')) {
        const parts = simulacao.nascimento.split('/');
        if (parts.length === 3) {
          const [dia, mes, ano] = parts;
          nascimentoISO = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
        }
      }

      dadosParaEnviar = {
        nome: simulacao.nome,
        cpf: simulacao.cpf,
        nascimento: nascimentoISO,
        cnh: simulacao.cnh,
        celular: simulacao.celular,
        placa: simulacao.placa,
        veiculo: simulacao.veiculo,
        ano: simulacao.ano,
        combustivel: simulacao.combustivel,
        valorVenda: desformatarMoedaParaDecimal(simulacao.valorVenda),
        entrada: desformatarMoedaParaDecimal(simulacao.entrada),
        financiado: desformatarMoedaParaDecimal(simulacao.financiado),
        observacoes: simulacao.observacoes,
        // Incluir dados aninhados da ficha se existirem
        ficha_id: simulacao.ficha_id ? {
          _id: simulacao.ficha_id._id,
          cliente: {
            ...(simulacao.ficha_id.cliente || {}),
            nome: simulacao.nome,
            cpf: simulacao.cpf,
            data_nascimento: nascimentoISO,
            celular: simulacao.celular,
            cnh: simulacao.cnh
          },
          dados_residenciais: simulacao.ficha_id.dados_residenciais || {},
          dados_profissionais: simulacao.ficha_id.dados_profissionais || {}
        } : undefined,
        // Incluir simulações dos bancos
        simulacoes_bancos: Object.keys(simulacoesBancos).reduce((acc, bancoId) => {
          const banco = simulacoesBancos[bancoId];
          if (banco && (banco.valorLiberado || (banco.opcoes && banco.opcoes.length > 0) || banco.recusada)) {
            const opcoesValidas = (banco.opcoes || []).filter(op => 
              op.qtdParcelas || op.valorParcela
            );
            // Incluir também quando o banco estiver marcado como recusada, mesmo sem valor/opções
            if (banco.valorLiberado || opcoesValidas.length > 0 || banco.recusada) {
              acc[bancoId] = {
                valorLiberado: banco.valorLiberado ? desformatarMoeda(banco.valorLiberado) : '',
                opcoes: opcoesValidas.map(op => ({
                  qtdParcelas: op.qtdParcelas,
                  valorParcela: desformatarMoeda(op.valorParcela)
                })),
                recusada: !!banco.recusada
              };
            }
          }
          return acc;
        }, {})
      };

      const response = await axios.put(
        `${API_URL}/simulacoes/${simulacaoId}`,
        dadosParaEnviar,
        {
          headers: { 'x-auth-token': token }
        }
      );

      if (response.data.success) {
        setMensagemSucesso('Simulação atualizada com sucesso!');
        
        // Feedback imediato no estado local antes de recarregar
        setSimulacao(prev => ({
          ...prev,
          ...response.data.data,
          // Manter o formato dd/mm/yyyy no estado local
          nascimento: simulacao.nascimento 
        }));

        // Recarregar a simulação para garantir sincronia total
        try {
          await carregarSimulacao();
        } catch (err) {
        }

        if (sucessoRef.current) {
          sucessoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => {
          setMensagemSucesso('');
        }, 3000);
      }
    } catch (error) {
      // Em caso de 429 ou falha temporária, salvar em fila local para não perder dados
      if (error?.response?.status === 429) {
        enqueueSave(simulacaoId, dadosParaEnviar);
        setMensagemErro('Servidor em alta carga. Salvo localmente e será reenviado automaticamente.');
        setTimeout(() => setMensagemErro(''), 7000);
      } else if (error?.code === 'ERR_NETWORK') {
        enqueueSave(simulacaoId, dadosParaEnviar);
        setMensagemErro('Sem conexão. Salvo localmente e será reenviado automaticamente.');
        setTimeout(() => setMensagemErro(''), 7000);
      } else {
        // Outros erros: também enfileirar para evitar perda acidental de dados
        enqueueSave(simulacaoId, dadosParaEnviar);
        setMensagemErro(error?.response?.data?.erro || 'Erro ao salvar simulação. Salvo localmente.');
        setTimeout(() => setMensagemErro(''), 7000);
      }
    } finally {
      setSalvando(false);
    }
  };

  const handleFinalizar = async () => {
    // Verificar se existem bancos cadastrados que não possuem simulação (valor liberado ou recusa)
    const bancosSemProposta = bancos.filter(banco => {
      const sim = simulacoesBancos[banco._id];
      if (!sim) return true;
      
      const temValor = sim.valorLiberado && sim.valorLiberado.trim() !== '' && sim.valorLiberado !== 'R$ 0,00';
      const estaRecusada = !!sim.recusada;
      
      return !temValor && !estaRecusada;
    });

    if (bancosSemProposta.length > 0) {
      setBancosPendentes(bancosSemProposta);
      setMostrarModalConfirmacaoBancos(true);
      return;
    }

    await executarFinalizacao();
  };

  const executarFinalizacao = async () => {
    try {
      setSalvando(true);
      setMostrarModalConfirmacaoBancos(false);
      const token = localStorage.getItem('token');

      const response = await axios.put(
        `${API_URL}/simulacoes/${simulacaoId}`,
        { status: 'Finalizada' },
        {
          headers: { 'x-auth-token': token }
        }
      );

      if (response.data.success) {
        setMensagemSucesso('Simulação finalizada com sucesso!');
        setSimulacao(prev => ({ ...prev, status: 'Finalizada' }));
        if (sucessoRef.current) {
          sucessoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => {
          onVoltar();
        }, 1500);
      }
    } catch (error) {
      if (error?.response?.status === 429) {
        setMensagemErro('Muitas requisições no servidor. Tente novamente em alguns segundos.');
        setTimeout(() => setMensagemErro(''), 5000);
      } else {
        setMensagemErro(error?.response?.data?.erro || 'Erro ao finalizar simulação');
        setTimeout(() => setMensagemErro(''), 5000);
      }
    } finally {
      setSalvando(false);
    }
  };

  // Determinar se a ficha foi preenchida pelo cliente (token usado/atualização pública) ou marcada como Preenchida
  const fichaPossuiDadosExtras = Boolean(
    simulacao?.ficha_id?.cliente?.rg ||
    simulacao?.ficha_id?.cliente?.email ||
    simulacao?.ficha_id?.cliente?.telefone ||
    simulacao?.ficha_id?.cliente?.endereco?.rua ||
    simulacao?.ficha_id?.dados_profissionais?.tipo_trabalho ||
    simulacao?.ficha_id?.dados_profissionais?.empresa
  );

  // Usar status foiPreenchida da ficha se disponível, caso contrário usar heurística anterior
  const fichaCompleta = Boolean(
    simulacao?.ficha_id && (
      fichaFoiPreenchida ||
      simulacao.ficha_id.token_usado === true ||
      !!simulacao.ficha_id.token_finalizado_em ||
      !!simulacao.ficha_id.ultima_atualizacao_publica ||
      simulacao.ficha_id.status === 'Preenchida' ||
      fichaPossuiDadosExtras
    )
  );

  // Desabilitar visualização completa sempre que a ficha não estiver completa
  useEffect(() => {
    if (!fichaCompleta && mostrarFichaCompleta) {
      setMostrarFichaCompleta(false);
    }
  }, [fichaCompleta, mostrarFichaCompleta]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Carregando detalhes da simulação...</p>
      </div>
    );
  }

  if (!simulacao) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Simulação não encontrada</p>
        <button onClick={onVoltar} className="btn-salvar">Voltar</button>
      </div>
    );
  }

  return (
    <div className="preencher-ficha-wrapper" style={{ 
      height: '100%', 
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: '#f0f0f0',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div className="preencher-ficha-container" style={{ 
        flex: 1,
        overflowY: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        background: '#f0f0f0',
        paddingBottom: '100px'
      }}>
        <style>{`
          .preencher-ficha-container::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        <div className="preencher-ficha-form" style={{ 
          maxWidth: '1200px', 
          width: '100%',
          padding: '0', 
          margin: '0 auto',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}>
        {mensagemSucesso && (
          <div ref={sucessoRef} style={{ 
            padding: '1rem', 
            background: '#e8f5e9', 
            border: '1px solid #4caf50',
            borderRadius: '4px',
            marginBottom: '2rem',
            color: '#2e7d32',
            fontSize: '12px'
          }}>
            {mensagemSucesso}
          </div>
        )}

        {mensagemErro && (
          <div style={{ 
            padding: '1rem', 
            background: '#fee2e2', 
            border: '1px solid #f44336',
            borderRadius: '4px',
            marginBottom: '2rem',
            color: '#7f1d1d',
            fontSize: '12px'
          }}>
            {mensagemErro}
          </div>
        )} 

        {/* Seção de Simulações por Banco - Análise Detalhada */}
        {bancos.length > 0 && bancoAtualIndex >= 0 && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            border: '1px solid #e0e0e0',
            padding: '16px',
            marginBottom: '8px'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '11px', color: '#999', fontWeight: 600, display: 'block', marginBottom: '8px' }}>
                Banco Selecionado:
              </label>
              <select
                value={bancos[bancoAtualIndex]?._id || ''}
                onChange={(e) => navegarBanco(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  fontSize: '14px',
                  fontWeight: 600,
                  background: 'white',
                  cursor: 'pointer',
                  color: '#333'
                }}
              >
                <option value="" disabled>Selecione um banco</option>
                {bancos.map((banco) => (
                  <option key={banco._id} value={banco._id}>
                    {banco.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Valor Liberado (único por banco) */}
            {bancoAtualIndex >= 0 && bancos[bancoAtualIndex] && simulacoesBancos[bancos[bancoAtualIndex]._id] && (
              <div style={{ 
                marginBottom: '12px', 
                padding: '8px 10px',
                background: '#fafafa',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                {simulacoesBancos[bancos[bancoAtualIndex]._id].recusada ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '54px' }}>
                    <button
                      onClick={() => toggleRecusada(bancos[bancoAtualIndex]._id)}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                        border: '2px solid #d32f2f',
                        background: '#fce4ec',
                        color: '#d32f2f',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      ×
                    </button>
                    <span style={{ fontSize: '13px', color: '#444', fontWeight: 700 }}>Simulação recusada</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <label style={{ 
                        fontSize: '11px', 
                        color: '#555', 
                        fontWeight: 600,
                        display: 'block',
                        marginBottom: '6px'
                      }}>
                        Valor Liberado
                      </label>
                      <input
                        type="text"
                        value={simulacoesBancos[bancos[bancoAtualIndex]._id].valorLiberado || ''}
                        onChange={(e) => atualizarValorLiberado(bancos[bancoAtualIndex]._id, e.target.value)}
                        placeholder="R$ 0,00"
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                          fontWeight: 600,
                          background: 'white',
                          color: '#333'
                        }}
                      />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#555', fontWeight: 600, whiteSpace: 'nowrap', paddingTop: '4px' }}>
                      <input
                        type="checkbox"
                        checked={!!simulacoesBancos[bancos[bancoAtualIndex]._id].recusada}
                        onChange={() => toggleRecusada(bancos[bancoAtualIndex]._id)}
                        style={{ width: '16px', height: '16px', accentColor: '#d32f2f' }}
                      />
                      Recusada
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Opções de Parcelas */}
            {bancos[bancoAtualIndex] && 
             simulacoesBancos[bancos[bancoAtualIndex]._id]?.opcoes && 
             simulacoesBancos[bancos[bancoAtualIndex]._id].opcoes.length > 0 &&
             !simulacoesBancos[bancos[bancoAtualIndex]._id].recusada && (
              <div style={{ 
                marginBottom: '8px', 
                padding: '10px',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ 
                  fontSize: '11px', 
                  color: '#666', 
                  fontWeight: 600,
                  marginBottom: '8px'
                }}>
                  Opções de Parcelas
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {simulacoesBancos[bancos[bancoAtualIndex]._id].opcoes.map((opcao, idx) => (
                    <div key={idx} style={{ 
                      display: 'flex', 
                      gap: '8px', 
                      alignItems: 'flex-end',
                      paddingBottom: idx < simulacoesBancos[bancos[bancoAtualIndex]._id].opcoes.length - 1 ? '8px' : '0',
                      borderBottom: idx < simulacoesBancos[bancos[bancoAtualIndex]._id].opcoes.length - 1 ? '1px solid #f3f4f6' : 'none'
                    }}>
                      <div style={{ flex: '0 0 140px' }}>
                        {idx === 0 && (
                          <label style={{ 
                            fontSize: '11px', 
                            color: '#666', 
                            fontWeight: 600,
                            display: 'block',
                            marginBottom: '6px'
                          }}>
                            Quantidade de Parcelas
                          </label>
                        )}
                        <select
                          value={opcao.qtdParcelas}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === 'remover') {
                              removerOpcaoParcela(bancos[bancoAtualIndex]._id, idx);
                            } else if (val === 'outro') {
                              const custom = prompt('Digite a quantidade de parcelas:', opcao.qtdParcelas || '48');
                              if (custom && !isNaN(custom) && parseInt(custom) > 0) {
                                atualizarOpcaoParcela(bancos[bancoAtualIndex]._id, idx, 'qtdParcelas', custom);
                              }
                            } else {
                              atualizarOpcaoParcela(bancos[bancoAtualIndex]._id, idx, 'qtdParcelas', val);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '6px',
                            border: '2px solid #e0e0e0',
                            fontSize: '14px',
                            fontWeight: 600,
                            background: 'white',
                            cursor: 'pointer',
                            color: '#333'
                          }}
                        >
                          <option value="12">12x</option>
                          <option value="24">24x</option>
                          <option value="36">36x</option>
                          <option value="48">48x</option>
                          <option value="60">60x</option>
                          <option value="outro">Outro...</option>
                          {idx > 0 && <option value="remover">❌ Remover</option>}
                        </select>
                      </div>

                      <div style={{ flex: 1 }}>
                        {idx === 0 && (
                          <label style={{ 
                            fontSize: '11px', 
                            color: '#666', 
                            fontWeight: 600,
                            display: 'block',
                            marginBottom: '6px'
                          }}>
                            Valor da Parcela
                          </label>
                        )}
                        <input
                          type="text"
                          value={getValorComGap(opcao.valorParcela)}
                          readOnly={isVendedor}
                          onChange={(e) => {
                             if (!isVendedor) {
                               atualizarOpcaoParcela(bancos[bancoAtualIndex]._id, idx, 'valorParcela', e.target.value)
                             }
                          }}
                          placeholder="R$ 0,00"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '6px',
                            border: '2px solid #e0e0e0',
                            fontSize: '14px',
                            fontWeight: 600,
                            background: isVendedor ? '#f5f5f5' : 'white',
                            color: '#333',
                            cursor: isVendedor ? 'default' : 'text'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!(bancoAtualIndex >= 0 && bancos[bancoAtualIndex] && simulacoesBancos[bancos[bancoAtualIndex]._id]?.recusada) && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                <button
                  onClick={() => adicionarOpcaoParcela(bancos[bancoAtualIndex]._id)}
                  style={{
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    fontSize: '24px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,123,255,0.3)',
                    transition: 'all 0.2s ease'
                  }}
                  disabled={!(bancoAtualIndex >= 0 && bancos[bancoAtualIndex])}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.1)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0,123,255,0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0,123,255,0.3)';
                  }}
                >
                  +
                </button>
              </div>
            )}
          </div>
        )}

        <div className="form-section" style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
          border: '1px solid #e0e0e0',
          padding: '16px',
          marginBottom: '8px'
        }}>
          {/* Título com Checkbox Mostrar Ficha Completa e Botão Resumo */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            {(() => {
                // Lógica de cálculo de Status para o Botão Resumo
                let temAprovado = false;

                // Verifica cada banco
                if (bancos && bancos.length > 0) {
                    for (const banco of bancos) {
                        const dados = simulacoesBancos[banco._id] || {};
                        const valorLiberado = dados.valorLiberado 
                            ? parseFloat(dados.valorLiberado.toString().replace(/[^\d,]/g, '').replace(',', '.')) 
                            : 0;

                        if (valorLiberado > 0) {
                            temAprovado = true;
                            break; 
                        }
                    }
                }

                // Define cor e texto com base na prioridade
                // Se tem aprovado -> Verde (Com possibilidade)
                // Se não tem -> Vermelho (Sem possibilidade)
                
                let btnColor = temAprovado ? '#27ae60' : '#e74c3c';
                let btnText = temAprovado ? 'Com possibilidade' : 'Sem possibilidade';

                return (
                    <button
                        onClick={() => setMostrarResumo(true)}
                        style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        background: btnColor, color: 'white', border: 'none',
                        padding: '6px 12px', borderRadius: '4px', cursor: 'pointer',
                        fontSize: '13px', fontWeight: 'bold'
                        }}
                        title={`Status Geral: ${btnText}`}
                    >
                        📋 Resumo
                    </button>
                );
            })()}

            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: fichaCompleta ? '#333' : '#999', cursor: fichaCompleta ? 'pointer' : 'not-allowed', userSelect: 'none' }}>
              <input
                type="checkbox"
                id="mostrarFichaCompleta"
                checked={mostrarFichaCompleta}
                onChange={(e) => setMostrarFichaCompleta(e.target.checked)}
                disabled={!fichaCompleta}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: fichaCompleta ? 'pointer' : 'not-allowed',
                  accentColor: '#3498db'
                }}
                title={!fichaCompleta ? 'Ficha não preenchida' : ''}
              />
              Mostrar completa
            </label>
          </div>
          
          <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
            <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Nome:</label>
            <input
              type="text"
              value={simulacao.nome || ''}
              onChange={(e) => handleChange('nome', e.target.value)}
              onDoubleClick={() => handleDuploClique('nome', simulacao.nome)}
              placeholder="Nome completo"
              style={{ 
                padding: '10px', 
                borderRadius: '8px', 
                border: '1px solid #e0e0e0', 
                fontSize: '13px',
                background: campoCopiadoFeedback === 'nome' ? '#c8e6c9' : 'white',
                transition: 'background 0.3s ease'
              }}
            />
          </div>

          <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
            <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>CPF:</label>
            <input
              type="text"
              value={simulacao.cpf || ''}
              onChange={(e) => handleChange('cpf', e.target.value)}
              onDoubleClick={() => handleDuploClique('cpf', simulacao.cpf)}
              placeholder="000.000.000-00"
              style={{ 
                padding: '10px', 
                borderRadius: '8px', 
                border: '1px solid #e0e0e0', 
                fontSize: '13px',
                background: campoCopiadoFeedback === 'cpf' ? '#c8e6c9' : 'white',
                transition: 'background 0.3s ease'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Nascimento:</label>
              <input
                type="text"
                value={simulacao.nascimento || ''}
                onChange={(e) => handleChange('nascimento', e.target.value)}
                onDoubleClick={() => handleDuploClique('nascimento', simulacao.nascimento)}
                placeholder="dd/mm/aaaa"
                style={{ 
                  padding: '10px', 
                  borderRadius: '8px', 
                  border: '1px solid #e0e0e0', 
                  fontSize: '13px',
                  background: campoCopiadoFeedback === 'nascimento' ? '#c8e6c9' : 'white',
                  transition: 'background 0.3s ease'
                }}
              />
            </div>

            <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>CNH:</label>
              <input
                type="text"
                value={simulacao.cnh || ''}
                onChange={(e) => handleChange('cnh', e.target.value)}
                onDoubleClick={() => handleDuploClique('cnh', simulacao.cnh)}
                placeholder="Ex: sim, não, vencida"
                style={{ 
                  padding: '10px', 
                  borderRadius: '8px', 
                  border: '1px solid #e0e0e0', 
                  fontSize: '13px',
                  background: campoCopiadoFeedback === 'cnh' ? '#c8e6c9' : 'white',
                  transition: 'background 0.3s ease'
                }}
              />
            </div>
          </div>

          <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
            <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Celular:</label>
            <input
              type="text"
              value={simulacao.celular || ''}
              onChange={(e) => handleChange('celular', e.target.value)}
              onDoubleClick={() => handleDuploClique('celular', simulacao.celular)}
              placeholder="(00) 00000-0000"
              style={{ 
                padding: '10px', 
                borderRadius: '8px', 
                border: '1px solid #e0e0e0', 
                fontSize: '13px',
                background: campoCopiadoFeedback === 'celular' ? '#c8e6c9' : 'white',
                transition: 'background 0.3s ease'
              }}
            />
          </div>

          {/* Campos extras quando ficha completa está marcada */}
          {mostrarFichaCompleta && fichaCompleta && (
            <>
              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>RG:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.cliente?.rg || ''}
                  onChange={(e) => handleNestedChange('ficha_id.cliente.rg', e.target.value)}
                  onDoubleClick={() => handleDuploClique('rg', simulacao.ficha_id?.cliente?.rg)}
                  placeholder="Número do RG"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'rg' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Email:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.cliente?.email || ''}
                  onChange={(e) => handleNestedChange('ficha_id.cliente.email', e.target.value)}
                  onDoubleClick={() => handleDuploClique('email', simulacao.ficha_id?.cliente?.email)}
                  placeholder="email@exemplo.com"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'email' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Telefone Fixo:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.cliente?.telefone || ''}
                  onChange={(e) => handleNestedChange('ficha_id.cliente.telefone', e.target.value)}
                  onDoubleClick={() => handleDuploClique('telefone', simulacao.ficha_id?.cliente?.telefone)}
                  placeholder="(00) 0000-0000"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'telefone' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Nome do Pai:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.cliente?.nome_pai || ''}
                  onChange={(e) => handleNestedChange('ficha_id.cliente.nome_pai', e.target.value)}
                  onDoubleClick={() => handleDuploClique('nome_pai', simulacao.ficha_id?.cliente?.nome_pai)}
                  placeholder="Nome completo do pai"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'nome_pai' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Nome da Mãe:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.cliente?.nome_mae || ''}
                  onChange={(e) => handleNestedChange('ficha_id.cliente.nome_mae', e.target.value)}
                  onDoubleClick={() => handleDuploClique('nome_mae', simulacao.ficha_id?.cliente?.nome_mae)}
                  placeholder="Nome completo da mãe"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'nome_mae' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Estado Civil:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.cliente?.estado_civil || ''}
                  onChange={(e) => handleNestedChange('ficha_id.cliente.estado_civil', e.target.value)}
                  onDoubleClick={() => handleDuploClique('estado_civil', simulacao.ficha_id?.cliente?.estado_civil)}
                  placeholder="Solteiro, Casado, etc"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'estado_civil' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Naturalidade:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.cliente?.naturalidade || ''}
                  onChange={(e) => handleNestedChange('ficha_id.cliente.naturalidade', e.target.value)}
                  onDoubleClick={() => handleDuploClique('naturalidade', simulacao.ficha_id?.cliente?.naturalidade)}
                  placeholder="Cidade de nascimento"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'naturalidade' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>
            </>
          )}
        </div>

        <div className="form-section" style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
          border: '1px solid #e0e0e0',
          padding: '16px',
          marginBottom: '8px'
        }}>
          {/* Marca/Modelo - Full Width */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '4px' }}>
            <input
              type="text"
              value={simulacao.veiculo || ''}
              onChange={(e) => handleChange('veiculo', e.target.value)}
              onDoubleClick={() => handleDuploClique('veiculo', simulacao.veiculo)}
              placeholder="FIAT UNO"
              style={{ 
                padding: '6px 8px', 
                borderRadius: '6px', 
                border: '1px solid rgb(224, 224, 224)', 
                fontSize: '12px',
                background: campoCopiadoFeedback === 'veiculo' ? '#c8e6c9' : 'white',
                transition: 'background 0.3s'
              }}
            />
          </div>

          {/* Grid compacto com 2 colunas */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {/* Placa */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '10px', color: '#999', fontWeight: 600 }}>Placa:</label>
              <input
                type="text"
                value={simulacao.placa ? simulacao.placa.replace(/-/g, '') : ''}
                onChange={(e) => handleChange('placa', e.target.value)}
                onDoubleClick={() => handleDuploClique('placa', simulacao.placa)}
                placeholder="ABC-1234"
                style={{ 
                  padding: '6px 8px', 
                  borderRadius: '6px', 
                  border: '1px solid #e0e0e0', 
                  fontSize: '12px',
                  background: campoCopiadoFeedback === 'placa' ? '#c8e6c9' : 'white',
                  transition: 'background 0.3s ease'
                }}
              />
            </div>

            {/* Ano */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '10px', color: '#999', fontWeight: 600 }}>Ano Fab/Mod:</label>
              <input
                type="text"
                value={simulacao.ano || ''}
                onChange={(e) => handleChange('ano', e.target.value)}
                onDoubleClick={() => handleDuploClique('ano', simulacao.ano)}
                placeholder="2020/2021"
                style={{ 
                  padding: '6px 8px', 
                  borderRadius: '6px', 
                  border: '1px solid #e0e0e0', 
                  fontSize: '12px',
                  background: campoCopiadoFeedback === 'ano' ? '#c8e6c9' : 'white',
                  transition: 'background 0.3s ease'
                }}
              />
            </div>

            {/* Valor Venda */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '10px', color: '#999', fontWeight: 600 }}>Valor Venda:</label>
              <input
                type="text"
                value={simulacao.valorVenda || ''}
                onChange={(e) => handleChange('valorVenda', e.target.value)}
                onDoubleClick={() => handleDuploClique('valorVenda', simulacao.valorVenda)}
                placeholder="R$ 0,00"
                style={{ 
                  padding: '6px 8px', 
                  borderRadius: '6px', 
                  border: '1px solid #e0e0e0', 
                  fontSize: '12px',
                  background: campoCopiadoFeedback === 'valorVenda' ? '#c8e6c9' : 'white',
                  transition: 'background 0.3s ease'
                }}
              />
            </div>

            {/* Entrada */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '10px', color: '#999', fontWeight: 600 }}>Entrada:</label>
              <input
                type="text"
                value={simulacao.entrada || ''}
                onChange={(e) => handleChange('entrada', e.target.value)}
                onDoubleClick={() => handleDuploClique('entrada', simulacao.entrada)}
                placeholder="R$ 0,00"
                style={{ 
                  padding: '6px 8px', 
                  borderRadius: '6px', 
                  border: '1px solid #e0e0e0', 
                  fontSize: '12px',
                  background: campoCopiadoFeedback === 'entrada' ? '#c8e6c9' : 'white',
                  transition: 'background 0.3s ease'
                }}
              />
            </div>

            {/* Combustível */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '10px', color: '#999', fontWeight: 600 }}>Combustível:</label>
              <input
                type="text"
                value={simulacao.combustivel || ''}
                onChange={(e) => handleChange('combustivel', e.target.value)}
                onDoubleClick={() => handleDuploClique('combustivel', simulacao.combustivel)}
                placeholder="Gasolina"
                style={{ 
                  padding: '6px 8px', 
                  borderRadius: '6px', 
                  border: '1px solid #e0e0e0', 
                  fontSize: '12px',
                  background: campoCopiadoFeedback === 'combustivel' ? '#c8e6c9' : 'white',
                  transition: 'background 0.3s ease'
                }}
              />
            </div>

            {/* Financiado */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '10px', color: '#999', fontWeight: 600 }}>Financiado:</label>
              <input
                type="text"
                value={simulacao.financiado || ''}
                readOnly
                style={{ 
                  background: campoCopiadoFeedback === 'financiado' ? '#a5d6a7' : '#e8f5e9',
                  fontWeight: '600',
                  color: '#2e7d32',
                  cursor: 'pointer',
                  padding: '6px 8px',
                  borderRadius: '6px',
                  border: '1px solid #c8e6c9',
                  fontSize: '12px',
                  transition: 'background 0.3s ease'
                }}
                onDoubleClick={() => handleDuploClique('financiado', simulacao.financiado)}
                placeholder="Auto"
              />
            </div>
          </div>
        </div>

        {/* Seções Dados Residenciais e Profissionais - Aparecem apenas se checkbox marcado */}
        {mostrarFichaCompleta && fichaCompleta && (
          <>
            <div className="form-section" style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              border: '1px solid #e0e0e0',
              padding: '16px',
              marginBottom: '8px'
            }}>
              <h3 className="form-section-title" style={{ fontSize: '13px', margin: 0, marginBottom: '12px', color: '#333', fontWeight: 700 }}>Dados Residenciais</h3>
              
              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Endereço Completo:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.cliente?.endereco ? 
                    `${simulacao.ficha_id.cliente.endereco.rua || ''} ${simulacao.ficha_id.cliente.endereco.numero || ''}, ${simulacao.ficha_id.cliente.endereco.bairro || ''}, ${simulacao.ficha_id.cliente.endereco.cidade || ''} - ${simulacao.ficha_id.cliente.endereco.estado || ''}`.trim() : ''}
                  onDoubleClick={() => handleDuploClique('endereco_completo', simulacao.ficha_id?.cliente?.endereco ? 
                    `${simulacao.ficha_id.cliente.endereco.rua || ''} ${simulacao.ficha_id.cliente.endereco.numero || ''}, ${simulacao.ficha_id.cliente.endereco.bairro || ''}, ${simulacao.ficha_id.cliente.endereco.cidade || ''} - ${simulacao.ficha_id.cliente.endereco.estado || ''}`.trim() : '')}
                  placeholder="Rua, Número, Bairro, Cidade - UF"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'endereco_completo' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>CEP:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.cliente?.endereco?.cep || ''}
                  onChange={(e) => handleNestedChange('ficha_id.cliente.endereco.cep', e.target.value)}
                  onDoubleClick={() => handleDuploClique('cep', simulacao.ficha_id?.cliente?.endereco?.cep)}
                  placeholder="00000-000"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'cep' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Complemento:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.cliente?.endereco?.complemento || ''}
                  onChange={(e) => handleNestedChange('ficha_id.cliente.endereco.complemento', e.target.value)}
                  onDoubleClick={() => handleDuploClique('complemento', simulacao.ficha_id?.cliente?.endereco?.complemento)}
                  placeholder="Apartamento, bloco, etc"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'complemento' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Tipo de Residência:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.dados_residenciais?.tipo_residencia || ''}
                  onChange={(e) => handleNestedChange('ficha_id.dados_residenciais.tipo_residencia', e.target.value)}
                  onDoubleClick={() => handleDuploClique('tipo_residencia', simulacao.ficha_id?.dados_residenciais?.tipo_residencia)}
                  placeholder="Própria, Alugada, etc"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'tipo_residencia' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Tempo de Residência:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.dados_residenciais?.tempo_residencia || ''}
                  onChange={(e) => handleNestedChange('ficha_id.dados_residenciais.tempo_residencia', e.target.value)}
                  onDoubleClick={() => handleDuploClique('tempo_residencia', simulacao.ficha_id?.dados_residenciais?.tempo_residencia)}
                  placeholder="Ex: 5 anos"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'tempo_residencia' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>
            </div>

            <div className="form-section" style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              border: '1px solid #e0e0e0',
              padding: '16px',
              marginBottom: '8px'
            }}>
              <h3 className="form-section-title" style={{ fontSize: '13px', margin: 0, marginBottom: '12px', color: '#333', fontWeight: 700 }}>Dados Profissionais</h3>
              
              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Empresa/Trabalho:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.dados_profissionais?.empresa || ''}
                  onChange={(e) => handleNestedChange('ficha_id.dados_profissionais.empresa', e.target.value)}
                  onDoubleClick={() => handleDuploClique('empresa', simulacao.ficha_id?.dados_profissionais?.empresa)}
                  placeholder="Nome da empresa"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'empresa' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>CNPJ da Empresa:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.dados_profissionais?.cnpj || ''}
                  onChange={(e) => handleNestedChange('ficha_id.dados_profissionais.cnpj', e.target.value)}
                  onDoubleClick={() => handleDuploClique('cnpj_empresa', simulacao.ficha_id?.dados_profissionais?.cnpj)}
                  placeholder="00.000.000/0000-00"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'cnpj_empresa' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Tipo de Trabalho:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.dados_profissionais?.tipo_trabalho || ''}
                  onChange={(e) => handleNestedChange('ficha_id.dados_profissionais.tipo_trabalho', e.target.value)}
                  onDoubleClick={() => handleDuploClique('tipo_trabalho', simulacao.ficha_id?.dados_profissionais?.tipo_trabalho)}
                  placeholder="CLT, Autônomo, etc"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'tipo_trabalho' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Endereço da Empresa:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.dados_profissionais?.endereco || ''}
                  onChange={(e) => handleNestedChange('ficha_id.dados_profissionais.endereco', e.target.value)}
                  onDoubleClick={() => handleDuploClique('endereco_empresa', simulacao.ficha_id?.dados_profissionais?.endereco)}
                  placeholder="Endereço completo da empresa"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'endereco_empresa' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Profissão/Cargo:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.dados_profissionais?.cargo || ''}
                  onChange={(e) => handleNestedChange('ficha_id.dados_profissionais.cargo', e.target.value)}
                  onDoubleClick={() => handleDuploClique('cargo', simulacao.ficha_id?.dados_profissionais?.cargo)}
                  placeholder="Cargo ocupado"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'cargo' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Tempo de Serviço:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.dados_profissionais?.tempo_servico || ''}
                  onChange={(e) => handleNestedChange('ficha_id.dados_profissionais.tempo_servico', e.target.value)}
                  onDoubleClick={() => handleDuploClique('tempo_servico', simulacao.ficha_id?.dados_profissionais?.tempo_servico)}
                  placeholder="Ex: 3 anos"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'tempo_servico' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Salário:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.dados_profissionais?.salario || ''}
                  onChange={(e) => handleNestedChange('ficha_id.dados_profissionais.salario', e.target.value)}
                  onDoubleClick={() => handleDuploClique('salario', simulacao.ficha_id?.dados_profissionais?.salario)}
                  placeholder="R$ 0,00"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'salario' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>

              <div className="campo-horizontal" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>Telefone Comercial:</label>
                <input
                  type="text"
                  value={simulacao.ficha_id?.dados_profissionais?.telefone || ''}
                  onChange={(e) => handleNestedChange('ficha_id.dados_profissionais.telefone', e.target.value)}
                  onDoubleClick={() => handleDuploClique('telefone_comercial', simulacao.ficha_id?.dados_profissionais?.telefone)}
                  placeholder="(00) 0000-0000"
                  style={{ 
                    padding: '10px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0', 
                    fontSize: '13px',
                    background: campoCopiadoFeedback === 'telefone_comercial' ? '#c8e6c9' : 'white',
                    cursor: 'text',
                    transition: 'background 0.3s ease'
                  }}
                />
              </div>
            </div>
          </>
        )}

        {/* Seção Observações - Sempre no final */}
        <div className="form-section" style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
          border: '1px solid #e0e0e0',
          padding: '16px',
          marginBottom: '8px'
        }}>
          
          <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '14px', color: '#333', fontWeight: 600 }}>Observações</h3>

          {/* Lista de Observações Anteriores */}
          {simulacao.observacoes && Array.isArray(simulacao.observacoes) && simulacao.observacoes.length > 0 && (
            <div style={{ marginBottom: '16px', maxHeight: '300px', overflowY: 'auto', paddingRight: '8px' }}>
              {simulacao.observacoes.map((obs, idx) => (
                <div key={obs._id || idx} style={{
                  background: '#f5f5f5',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  borderLeft: '3px solid #2196f3',
                  position: 'relative'
                }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                    <strong>{obs.usuario_nome}</strong>
                    <br/>
                    {new Date(obs.criadoEm).toLocaleString('pt-BR')}
                  </div>
                  <div style={{ fontSize: '13px', color: '#333', wordBreak: 'break-word' }}>
                    {obs.texto}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Campo para Adicionar Nova Observação */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <textarea
                value={novaObservacao}
                onChange={(e) => setNovaObservacao(e.target.value)}
                placeholder="Digite uma observação..."
                rows="3"
                style={{ 
                  width: '100%',
                  padding: '10px', 
                  borderRadius: '6px', 
                  border: '1px solid #ddd', 
                  fontSize: '13px', 
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <button
              onClick={adicionarObservacao}
              disabled={adicionandoObservacao || !novaObservacao.trim()}
              style={{
                padding: '10px 16px',
                background: adicionandoObservacao ? '#ccc' : '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: adicionandoObservacao ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '13px',
                minWidth: '80px'
              }}
            >
              {adicionandoObservacao ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </div>
      </div>

      {/* Botões fixos no rodapé */}
      <div style={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderTop: '2px solid #e0e0e0',
        padding: '12px 16px',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
        zIndex: 1000
      }}>
          <button 
            onClick={onVoltar} 
            className="btn-voltar"
            style={{
              background: 'white',
              color: '#333',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '13px',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Voltar
          </button>
          <button 
            onClick={handleSalvar} 
            className="btn-salvar"
            disabled={salvando}
            style={{
              background: salvando ? '#95a5a6' : '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: salvando ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              flex: 1
            }}
          >
            {salvando ? (
              <>
                <span
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255,255,255,0.6)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'spin 0.8s linear infinite'
                  }}
                />
                Salvando...
              </>
            ) : 'Salvar'}
          </button>

          {/* Indicador de saves pendentes para esta simulação */}
          {simulacaoId && (() => {
            const q = loadPendingSaves();
            const count = q[simulacaoId] ? q[simulacaoId].length : 0;
            return count > 0 ? (
              <div style={{ marginLeft: '12px', padding: '6px 10px', background: '#fff7cd', border: '1px solid #fde68a', borderRadius: '8px', color: '#92400e', fontWeight: 700 }}>
                {count} pendente(s) — reenviando
              </div>
            ) : null;
          })()}

          {simulacao?.status !== 'Finalizada' && (
            <button 
              onClick={handleFinalizar} 
              disabled={salvando}
              style={{
                background: salvando ? '#95a5a6' : '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: salvando ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              ✓ Finalizar
            </button>
          )}
          <style>{`@keyframes spin {from {transform: rotate(0deg);} to {transform: rotate(360deg);} }`}</style>
        </div>

        {/* Modal de Confirmação de Bancos Pendentes */}
        {mostrarModalConfirmacaoBancos && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
              overflow: 'hidden'
            }}>
              {/* Header do Modal */}
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #eee',
                background: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#fff3cd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  ⚠️
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', color: '#856404', fontWeight: 700 }}>Atenção: Bancos Pendentes</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Verifique as propostas antes de finalizar</p>
                </div>
              </div>

              {/* Conteúdo com scroll */}
              <div style={{
                padding: '20px',
                overflowY: 'auto',
                flex: 1,
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#444'
              }}>
                <p style={{ marginBottom: '16px', fontWeight: 600 }}>
                  Os seguintes bancos cadastrados no sistema estão sem parecer, cotação ou indicação de recusa:
                </p>
                
                <div style={{ 
                  background: '#fcfcfc', 
                  border: '1px solid #f0f0f0', 
                  borderRadius: '12px',
                  padding: '12px',
                  marginBottom: '20px'
                }}>
                  {bancosPendentes.map((banco, idx) => (
                    <div key={banco._id} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px',
                      padding: '8px 0',
                      borderBottom: idx < bancosPendentes.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }}>
                      <span style={{ color: '#c14427', fontWeight: 700 }}>•</span>
                      <span style={{ fontWeight: 600 }}>{banco.nome}</span>
                    </div>
                  ))}
                </div>

                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                  Deseja finalizar a simulação mesmo assim? Esta ação não poderá ser desfeita e os dados dos bancos acima não aparecerão para o vendedor.
                </p>
              </div>

              {/* Rodapé do Modal */}
              <div style={{
                padding: '16px 20px',
                borderTop: '1px solid #eee',
                background: '#f8f9fa',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => setMostrarModalConfirmacaoBancos(false)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    background: 'white',
                    color: '#666',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Voltar e Preencher
                </button>
                <button
                  onClick={executarFinalizacao}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#27ae60',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Confirmar e Finalizar
                </button>
              </div>
            </div>
          </div>
        )}

            {/* Modal de Resumo */}
            {mostrarResumo && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 9999, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        background: 'white', borderRadius: '8px',
                        width: '90%', maxWidth: '1000px', maxHeight: '90vh',
                        display: 'flex', flexDirection: 'column',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{ padding: '15px 20px', borderBottom: '1px solid #eee' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h3 style={{ margin: 0, color: '#2c3e50' }}>Resumo Geral de Simulações</h3>
                                <button 
                                    onClick={() => setMostrarResumo(false)}
                                    style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}
                                >
                                    &times;
                                </button>
                            </div>
                            
                            <div style={{ 
                                display: 'flex', flexDirection: 'column', gap: '10px',
                                background: '#f8f9fa', padding: '15px', borderRadius: '6px', fontSize: '14px' 
                            }}>
                                <div style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '10px', marginBottom: '5px' }}>
                                    <span style={{ color: '#666', display: 'block', fontSize: '12px', marginBottom: '2px' }}>Veículo</span>
                                    <strong style={{ color: '#2c3e50', fontSize: '16px', display: 'block' }}>{simulacao.veiculo || 'N/A'}</strong>
                                </div>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                    <div>
                                        <span style={{ color: '#666', display: 'block', fontSize: '12px' }}>Valor Venda</span>
                                        <strong style={{ color: '#27ae60', fontSize: '15px' }}>{simulacao.valorVenda || 'R$ 0,00'}</strong>
                                    </div>
                                    <div>
                                        <span style={{ color: '#666', display: 'block', fontSize: '12px' }}>Valor Financiado</span>
                                        <strong style={{ color: '#2980b9', fontSize: '15px' }}>{simulacao.financiado || 'R$ 0,00'}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div style={{ overflow: 'auto', padding: '20px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                                        <th style={{ padding: '10px', textAlign: 'left', color: '#555' }}>Banco</th>
                                        <th style={{ padding: '10px', textAlign: 'left', color: '#555' }}>Valor liberado</th>
                                        {[60, 48, 36, 24, 12].map(p => {
                                            const hasData = bancos.some(b => {
                                                const dados = simulacoesBancos[b._id] || {};
                                                return (dados.opcoes || []).some(o => o.qtdParcelas == p);
                                            });
                                            return hasData ? <th key={p} style={{ padding: '10px', textAlign: 'center', color: '#555' }}>{p}x</th> : null;
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...bancos].sort((a, b) => {
                                        const dadosA = simulacoesBancos[a._id] || {};
                                        const dadosB = simulacoesBancos[b._id] || {};
                                        
                                        const getValor = (v) => {
                                            if (!v || typeof v !== 'string') return 0;
                                            return parseFloat(v.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
                                        };

                                        const valA = getValor(dadosA.valorLiberado);
                                        const valB = getValor(dadosB.valorLiberado);
                                        
                                        const isApprovedA = valA > 0;
                                        const isApprovedB = valB > 0;
                                        const recusadaA = !!dadosA.recusada;
                                        const recusadaB = !!dadosB.recusada;

                                        // 1. Aprovados primeiro (pelo maior valor)
                                        if (isApprovedA && isApprovedB) return valB - valA;
                                        if (isApprovedA) return -1;
                                        if (isApprovedB) return 1;

                                        // 2. Recusados (no meio)
                                        if (recusadaA && !recusadaB) return -1;
                                        if (!recusadaA && recusadaB) return 1;

                                        // 3. Demais (final)
                                        return 0;
                                    }).map(banco => {
                                        const dados = simulacoesBancos[banco._id] || {};
                                        const opcoes = dados.opcoes || [];
                                        
                                        const getVal = (q) => {
                                            const opt = opcoes.find(o => o.qtdParcelas == q);
                                            return opt ? getValorComGap(opt.valorParcela) : ''; 
                                        };

                                        return (
                                            <tr key={banco._id} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '10px', fontWeight: 600 }}>{banco.nome}</td>
                                                <td style={{ padding: '10px' }}>
                                                    {dados.recusada ? (
                                                        <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>✕</span>
                                                    ) : (
                                                        dados.valorLiberado || ''
                                                    )}
                                                </td>
                                                {[60, 48, 36, 24, 12].map(p => {
                                                    const hasData = bancos.some(b => {
                                                        const d = simulacoesBancos[b._id] || {};
                                                        return (d.opcoes || []).some(o => o.qtdParcelas == p);
                                                    });
                                                    return hasData ? <td key={p} style={{ padding: '10px', textAlign: 'center' }}>{getVal(p)}</td> : null;
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
      </div>
    </div>
  );
}

export default DetalhesSimulacao;
